// What Running Order needs to run a puzzle.
//
// Long-lived records store TMDB ids and read names, dates and image paths from
// the refreshed title table (CLAUDE.md: Cached TMDB data), so a film here is
// already resolved: whatever the server hands over is what the player sees.

// **On the board a film has no year.** The year is the answer, so the server
// sends it only once a game is over (ro_check), and the board never holds one
// -- not even in a screen reader's label.
export interface RunningOrderFilm {
  id: number;            // TMDB id — the identity a guess is recorded against
  title: string;         // the display name if one is set, otherwise TMDB's
  poster: string | null; // a TMDB image path; null falls back to a placeholder
}

/** A film in the answer, once the game is over: now with its year. */
export interface AnswerFilm extends RunningOrderFilm {
  year: number;
  /** 1-12: the month of the same date the order is built on (TMDB's release date). */
  month?: number;
}

/** What the board needs, as the server sends it (ro_board). */
export interface RunningOrderBoard {
  number: number;
  /** A queued daily reached with the test code: not a puzzle players can see. */
  test: boolean;
  films: RunningOrderFilm[];   // in the stored shuffle
  startOrder: number[];        // the stored shuffle every player starts from
}

/** The server's verdict on every guess so far (ro_check). */
export interface CheckResult {
  marks: GuessMarks[];         // one row per guess, in order
  finished: boolean;
  won: boolean;
  /** The correct order, oldest first, with years: only once finished. */
  answer?: AnswerFilm[];
}

export type GuessMarks = boolean[];        // one per slot: was the film in the right place

export interface FinishedGame {
  won: boolean;
  guesses: GuessMarks[];       // one row per guess, in order
}

export const MAX_GUESSES = 3;

// A poster at the size the layout needs, straight from TMDB's CDN: never
// downloaded, re-hosted or resized by us.
export function posterUrl(path: string | null, size: 'w185' | 'w342' = 'w342'): string | null {
  return path ? `https://image.tmdb.org/t/p/${size}${path}` : null;
}

// Which slots hold the right film. The game asks the server instead (it does
// not know the right order); this is the same rule, kept for the tests and for
// the How it works examples, which are static.
export function marksFor(order: number[], correct: number[]): GuessMarks {
  return order.map((id, i) => id === correct[i]);
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * "May 2013" under a film on the result screen (owner, 11 October): two films
 * from one year otherwise read as a tie. The month comes with the answer, from
 * the same date the order is built on; the year alone if it is ever missing.
 */
export function releasedLabel(film: { year: number; month?: number }): string {
  return film.month && film.month >= 1 && film.month <= 12 ? `${MONTHS[film.month - 1]} ${film.year}` : String(film.year);
}
